import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Paperclip, X } from "lucide-react";
import type { ClaimPhoto, Document } from "@/api/generated/client/models";

export interface EmailAttachmentInput {
    filename: string;
    mimeType: string;
    contentBase64: string;
}

export interface EmailRecipientSuggestion {
    email: string;
    label: string;
}

interface PendingAttachment {
    id: string;
    filename: string;
    mimeType: string;
    sizeBytes: number;
    file?: File;
    documentId?: number;
    photoId?: number;
}

const MAX_ATTACHMENT_BYTES = 18 * 1024 * 1024;
const MAX_ATTACHMENTS = 10;

function readBlobAsBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result !== "string") {
                reject(new Error("Could not read attachment."));
                return;
            }
            const separator = result.indexOf(",");
            if (separator < 0) {
                reject(new Error("Could not read attachment."));
                return;
            }
            resolve(result.slice(separator + 1));
        };
        reader.onerror = () => reject(reader.error ?? new Error("Could not read attachment."));
        reader.readAsDataURL(blob);
    });
}

function formatFileSize(sizeBytes: number): string {
    if (sizeBytes < 1024) return `${sizeBytes} B`;
    if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(0)} KB`;
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface ComposeEmailModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSend: (params: {
        to: Array<{ email: string; name?: string }>;
        cc?: Array<{ email: string; name?: string }>;
        bcc?: Array<{ email: string; name?: string }>;
        subject: string;
        bodyText?: string;
        bodyHtml?: string;
        attachments?: EmailAttachmentInput[];
    }) => Promise<void>;
    isSending: boolean;
    fromEmail?: string;
    defaultBody?: string;
    claimDocuments?: Array<Pick<Document, "id" | "filename" | "mimeType" | "sizeBytes">>;
    documentsLoading?: boolean;
    getDocumentBlob?: (documentId: number) => Promise<Blob>;
    claimPhotos?: Array<Pick<ClaimPhoto, "id" | "filename" | "mimeType" | "sizeBytes">>;
    photosLoading?: boolean;
    getPhotoBlob?: (photoId: number) => Promise<Blob>;
    recipientSuggestions?: EmailRecipientSuggestion[];
}

export function ComposeEmailModal({
    open,
    onOpenChange,
    onSend,
    isSending,
    fromEmail,
    defaultBody,
    claimDocuments = [],
    documentsLoading = false,
    getDocumentBlob,
    claimPhotos = [],
    photosLoading = false,
    getPhotoBlob,
    recipientSuggestions = [],
}: ComposeEmailModalProps) {
    const [to, setTo] = useState("");
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
    const [libraryFileToAttach, setLibraryFileToAttach] = useState("");
    const [isPreparingAttachments, setIsPreparingAttachments] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachmentSequence = useRef(0);
    const isBusy = isSending || isPreparingAttachments;
    const recipientSuggestionListId = useId();
    const totalAttachmentBytes = attachments.reduce((sum, attachment) => sum + (attachment.sizeBytes ?? 0), 0);

    useEffect(() => {
        if (open && defaultBody?.trim()) {
            setBody((prevBody) => (prevBody.trim() ? prevBody : defaultBody));
        }
    }, [open, defaultBody]);

    // Basic email shape check, matching the pattern used elsewhere (e.g. SendFormModal).
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const parseEmails = (input: string): Array<{ email: string; name?: string }> => {
        if (!input.trim()) return [];
        return input.split(",").map((email) => ({ email: email.trim() })).filter((e) => e.email);
    };

    const resetForm = () => {
        setTo("");
        setCc("");
        setBcc("");
        setSubject("");
        setBody("");
        setShowCc(false);
        setShowBcc(false);
        setAttachments([]);
        setLibraryFileToAttach("");
        setError(null);
    };

    const handleFilesSelected = (files: FileList | null) => {
        if (!files?.length) return;
        const selectedFiles = Array.from(files);
        if (selectedFiles.some((file) => file.size === 0)) {
            setError("Empty files cannot be attached.");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        const totalBytes = attachments.reduce((sum, attachment) => sum + attachment.sizeBytes, 0)
            + selectedFiles.reduce((sum, file) => sum + file.size, 0);
        if (attachments.length + selectedFiles.length > MAX_ATTACHMENTS) {
            setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
            return;
        }
        if (totalBytes > MAX_ATTACHMENT_BYTES) {
            setError("Attachments cannot exceed 18 MB total.");
            return;
        }

        setError(null);
        setAttachments((current) => [
            ...current,
            ...selectedFiles.map((file) => ({
                id: `file-${attachmentSequence.current++}`,
                filename: file.name,
                mimeType: file.type || "application/octet-stream",
                sizeBytes: file.size,
                file,
            })),
        ]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // Option values are "document-<id>" / "photo-<id>" so one selector can cover both libraries.
    const handleLibraryFileSelected = (value: string) => {
        const [kind, rawId] = value.split("-");
        const libraryId = Number(rawId);
        const item = kind === "photo"
            ? claimPhotos.find((photo) => photo.id === libraryId)
            : claimDocuments.find((document) => document.id === libraryId);
        if (!item) return;
        if (attachments.some((attachment) => (kind === "photo" ? attachment.photoId : attachment.documentId) === libraryId)) {
            setLibraryFileToAttach("");
            return;
        }
        if (attachments.length >= MAX_ATTACHMENTS) {
            setError(`You can attach up to ${MAX_ATTACHMENTS} files.`);
            setLibraryFileToAttach("");
            return;
        }
        const sizeBytes = item.sizeBytes ?? 0;
        if (sizeBytes === 0) {
            setError("Empty files cannot be attached.");
            setLibraryFileToAttach("");
            return;
        }
        const totalBytes = attachments.reduce((sum, attachment) => sum + attachment.sizeBytes, 0) + sizeBytes;
        if (totalBytes > MAX_ATTACHMENT_BYTES) {
            setError("Attachments cannot exceed 18 MB total.");
            setLibraryFileToAttach("");
            return;
        }

        setError(null);
        setAttachments((current) => [...current, {
            id: value,
            filename: item.filename,
            mimeType: item.mimeType || "application/octet-stream",
            sizeBytes,
            ...(kind === "photo" ? { photoId: libraryId } : { documentId: libraryId }),
        }]);
        setLibraryFileToAttach("");
    };

    // Reset transient form state whenever the modal closes so a cancelled draft
    // never leaks into the next compose session (incl. for a different recipient).
    const handleOpenChange = (next: boolean) => {
        if (!next) resetForm();
        onOpenChange(next);
    };

    const handleSend = async () => {
        const toRecipients = parseEmails(to);
        if (toRecipients.length === 0) return;

        const trimmedSubject = subject.trim();
        const trimmedBody = body.trim();
        if (!trimmedSubject || !trimmedBody) return;

        // Validate every recipient up-front so the user gets immediate, specific
        // feedback instead of a generic server-side failure toast after sending.
        const ccRecipients = showCc ? parseEmails(cc) : [];
        const bccRecipients = showBcc ? parseEmails(bcc) : [];
        const invalid = [...toRecipients, ...ccRecipients, ...bccRecipients].find(
            (r) => !EMAIL_REGEX.test(r.email),
        );
        if (invalid) {
            setError(`"${invalid.email}" is not a valid email address.`);
            return;
        }
        setError(null);

        try {
            setIsPreparingAttachments(true);
            const attachmentPayload = await Promise.all(attachments.map(async (attachment) => {
                let blob: Blob | null = attachment.file ?? null;
                if (!blob && attachment.documentId != null && getDocumentBlob) {
                    try {
                        blob = await getDocumentBlob(attachment.documentId);
                    } catch {
                        throw new Error(`Could not load ${attachment.filename}.`);
                    }
                }
                if (!blob && attachment.photoId != null && getPhotoBlob) {
                    try {
                        blob = await getPhotoBlob(attachment.photoId);
                    } catch {
                        throw new Error(`Could not load ${attachment.filename}.`);
                    }
                }
                if (!blob) throw new Error(`Could not load ${attachment.filename}.`);
                return {
                    filename: attachment.filename,
                    mimeType: attachment.mimeType || blob.type || "application/octet-stream",
                    contentBase64: await readBlobAsBase64(blob),
                    sizeBytes: blob.size,
                };
            }));
            const totalBytes = attachmentPayload.reduce((sum, attachment) => sum + attachment.sizeBytes, 0);
            if (totalBytes > MAX_ATTACHMENT_BYTES) {
                throw new Error("Attachments cannot exceed 18 MB total.");
            }

            await onSend({
                to: toRecipients,
                cc: showCc ? ccRecipients : undefined,
                bcc: showBcc ? bccRecipients : undefined,
                subject: trimmedSubject,
                bodyText: trimmedBody,
                ...(attachmentPayload.length > 0
                    ? { attachments: attachmentPayload.map(({ filename, mimeType, contentBase64 }) => ({ filename, mimeType, contentBase64 })) }
                    : {}),
            });

            // Only reset and close on success
            resetForm();
            onOpenChange(false);
        } catch (error) {
            console.error("Send email failed:", error);
            if (error instanceof Error && (error.message.startsWith("Could not ") || error.message.startsWith("Attachments cannot"))) {
                setError(error.message);
            }
        } finally {
            setIsPreparingAttachments(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-[700px] max-h-[90dvh] md:max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5" />
                        Compose Email
                    </DialogTitle>
                    <DialogDescription>
                        {fromEmail
                            ? `Send an email from your connected Gmail account: ${fromEmail}`
                            : "Send an email from your connected Gmail account."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* To Field */}
                    <div className="space-y-2">
                        <Label htmlFor="to" className="text-sm font-medium">
                            To <span className="text-destructive">*</span>
                        </Label>
                        <Input
                            id="to"
                            placeholder="recipient@example.com, another@example.com"
                            list={recipientSuggestions.length > 0 ? recipientSuggestionListId : undefined}
                            value={to}
                            onChange={(e) => { setTo(e.target.value); setError(null); }}
                            disabled={isBusy}
                        />
                        {recipientSuggestions.length > 0 && (
                            <datalist id={recipientSuggestionListId}>
                                {recipientSuggestions.map((suggestion) => (
                                    <option key={suggestion.email} value={suggestion.email} label={suggestion.label} />
                                ))}
                            </datalist>
                        )}
                        <p className="text-xs text-muted-foreground">
                            Separate multiple email addresses with commas
                        </p>
                    </div>

                    {/* CC/BCC Toggle Buttons */}
                    <div className="flex gap-2">
                        {!showCc && <Button type="button" variant="outline" size="sm" onClick={() => setShowCc(true)} disabled={isBusy}>Add Cc</Button>}
                        {!showBcc && <Button type="button" variant="outline" size="sm" onClick={() => setShowBcc(true)} disabled={isBusy}>Add Bcc</Button>}
                    </div>

                    {showCc && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="cc" className="text-sm font-medium">Cc</Label>
                                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowCc(false); setCc(""); }} disabled={isBusy} aria-label="Remove Cc field" title="Remove Cc field">
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                            <Input id="cc" placeholder="cc@example.com" value={cc} onChange={(e) => { setCc(e.target.value); setError(null); }} disabled={isBusy} />
                        </div>
                    )}

                    {showBcc && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="bcc" className="text-sm font-medium">Bcc</Label>
                                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowBcc(false); setBcc(""); }} disabled={isBusy} aria-label="Remove Bcc field" title="Remove Bcc field">
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                            <Input id="bcc" placeholder="bcc@example.com" value={bcc} onChange={(e) => { setBcc(e.target.value); setError(null); }} disabled={isBusy} />
                        </div>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="subject" className="text-sm font-medium">Subject <span className="text-destructive">*</span></Label>
                        <Input id="subject" placeholder="Email subject" value={subject} onChange={(e) => setSubject(e.target.value)} disabled={isBusy} />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="body" className="text-sm font-medium">Message <span className="text-destructive">*</span></Label>
                        <Textarea id="body" placeholder="Type your message here..." className="min-h-[200px] resize-none" value={body} onChange={(e) => setBody(e.target.value)} disabled={isBusy} />
                    </div>

                    <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                            <Label>Attachments</Label>
                            {attachments.length > 0 && (
                                <span
                                    className={`text-xs ${totalAttachmentBytes > MAX_ATTACHMENT_BYTES ? "text-destructive" : "text-muted-foreground"}`}
                                >
                                    {attachments.length}/{MAX_ATTACHMENTS} files · {formatFileSize(totalAttachmentBytes)} / {formatFileSize(MAX_ATTACHMENT_BYTES)}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <input ref={fileInputRef} type="file" multiple className="sr-only" aria-label="Upload files" onChange={(event) => handleFilesSelected(event.target.files)} disabled={isBusy} />
                            {(claimDocuments.length > 0 || claimPhotos.length > 0) && (
                                <div className="min-w-0 flex-1">
                                    <Select
                                        value={libraryFileToAttach}
                                        onValueChange={(value) => {
                                            setLibraryFileToAttach(value);
                                            if (value) handleLibraryFileSelected(value);
                                        }}
                                        disabled={isBusy || documentsLoading || photosLoading}
                                    >
                                        <SelectTrigger aria-label="Claim file to attach" className="w-full">
                                            <SelectValue placeholder="Select a claim document or photo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {claimDocuments.length > 0 && (
                                                <SelectGroup>
                                                    <SelectLabel>Documents</SelectLabel>
                                                    {claimDocuments.map((document) => (
                                                        <SelectItem key={`document-${document.id}`} value={`document-${document.id}`} disabled={!getDocumentBlob}>{document.filename}</SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            )}
                                            {claimPhotos.length > 0 && (
                                                <SelectGroup>
                                                    <SelectLabel>Photos</SelectLabel>
                                                    {claimPhotos.map((photo) => (
                                                        <SelectItem key={`photo-${photo.id}`} value={`photo-${photo.id}`} disabled={!getPhotoBlob}>{photo.filename}</SelectItem>
                                                    ))}
                                                </SelectGroup>
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isBusy}>
                                <Paperclip className="mr-2 h-4 w-4" /> Upload files
                            </Button>
                        </div>
                        {(documentsLoading || photosLoading) && <p className="text-xs text-muted-foreground">Loading claim files...</p>}
                        {attachments.length > 0 && (
                            <ul className="divide-y rounded border" aria-label="Attached files">
                                {attachments.map((attachment) => (
                                    <li key={attachment.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                                        <span className="min-w-0 flex-1 truncate" title={attachment.filename}>{attachment.filename}</span>
                                        <span className="shrink-0 text-xs text-muted-foreground">{attachment.sizeBytes ? formatFileSize(attachment.sizeBytes) : "Stored document"}</span>
                                        <Button type="button" variant="ghost" size="sm" onClick={() => setAttachments((current) => current.filter((item) => item.id !== attachment.id))} disabled={isBusy} aria-label={`Remove ${attachment.filename}`} title={`Remove ${attachment.filename}`}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Validation error */}
                    {error && (
                        <p className="text-sm text-destructive break-words" role="alert">
                            {error}
                        </p>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isBusy}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSend}
                        disabled={!to.trim() || !subject.trim() || !body.trim() || isBusy}
                        className="gap-2"
                    >
                        {isBusy ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {isPreparingAttachments ? "Preparing attachments..." : "Sending..."}
                            </>
                        ) : (
                            <>
                                <Mail className="h-4 w-4" />
                                Send Email
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
